import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Card,
  Button,
  Badge,
  Form,
  Row,
  Col,
  Spinner,
  Alert,
  Modal,
  Pagination,
  InputGroup,
} from "react-bootstrap";
import {
  FaPlus,
  FaEdit,
  FaTrash,
  FaBook,
  FaFolder,
  FaFolderOpen,
  FaChevronRight,
  FaChevronDown,
  FaArrowLeft,
  FaCheckCircle,
  FaCopy,
  FaTimesCircle,
  FaSearch,
  FaChartLine,
  FaChevronCircleRight,
} from "react-icons/fa";
import { toast } from "react-toastify";
import Header from "../../../components/common/Header";
import CourseService from "../../../services/courseService";
import AdminNotesService from "../../../services/adminNotesService";
import ImportNotesModal from "../../../components/admin/notes/ImportNotesModal";
import "../../../styles/notes-admin.css";

const NotesManagement = () => {
  const navigate = useNavigate();

  // State for topics and courses
  const [topics, setTopics] = useState([]);
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingCourses, setLoadingCourses] = useState(true);
  const [selectedCourse, setSelectedCourse] = useState("");
  const [selectedCourseDetails, setSelectedCourseDetails] = useState(null);
  const [expandedItems, setExpandedItems] = useState({});
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedTopic, setSelectedTopic] = useState(null);
  const [error, setError] = useState(null);
  const [showImportModal, setShowImportModal] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);

  // Pagination and search states
  const [currentPage, setCurrentPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState("");
  const [coursesPerPage] = useState(6);

  // Statistics
  const [stats, setStats] = useState({
    totalFolders: 0,
    totalTopics: 0,
    totalBlocks: 0,
    totalExercises: 0,
  });

  // Load courses on mount
  useEffect(() => {
    loadCourses();
  }, []);

  // Load hierarchy when course changes
  useEffect(() => {
    if (selectedCourse) {
      loadHierarchy(selectedCourse);
      loadCourseDetails(selectedCourse);
    } else {
      setTopics([]);
      setStats({
        totalFolders: 0,
        totalTopics: 0,
        totalBlocks: 0,
        totalExercises: 0,
      });
      setLoading(false);
    }
  }, [selectedCourse]);

  // Load course details
  const loadCourseDetails = async (courseId) => {
    try {
      const course = courses.find((c) => c.id === parseInt(courseId));
      setSelectedCourseDetails(course);
    } catch (error) {
      console.error("Failed to load course details:", error);
    }
  };

  // Load courses
  const loadCourses = async () => {
    try {
      setLoadingCourses(true);
      const response = await CourseService.getAllCourses();
      let coursesData = [];
      if (response && response.data && Array.isArray(response.data)) {
        coursesData = response.data;
      } else if (response && Array.isArray(response)) {
        coursesData = response;
      }
      setCourses(coursesData);
    } catch (error) {
      console.error("Failed to load courses:", error);
      setError("Failed to load courses: " + error.message);
    } finally {
      setLoadingCourses(false);
    }
  };

  // Load hierarchy and calculate stats
  const loadHierarchy = async (courseId) => {
    try {
      setLoading(true);
      setError(null);
      const response = await AdminNotesService.getHierarchy(courseId);
      const hierarchyData = response.data || [];
      setTopics(hierarchyData);
      calculateStats(hierarchyData);
    } catch (error) {
      console.error("Failed to load hierarchy:", error);
      setError(
        "Failed to load notes: " +
          (error.response?.data?.message || error.message),
      );
      setTopics([]);
    } finally {
      setLoading(false);
    }
  };

  // Calculate statistics from hierarchy
  const calculateStats = (items) => {
    let folders = 0;
    let topics = 0;

    const countRecursive = (nodes) => {
      nodes.forEach((node) => {
        if (node.content_type === "folder") {
          folders++;
        } else {
          topics++;
        }
        if (node.children && node.children.length > 0) {
          countRecursive(node.children);
        }
      });
    };

    countRecursive(items);

    setStats({
      totalFolders: folders,
      totalTopics: topics,
      totalBlocks: 0,
      totalExercises: 0,
    });
  };

  // Toggle folder expand/collapse
  const toggleExpand = (id) => {
    setExpandedItems((prev) => ({
      ...prev,
      [id]: !prev[id],
    }));
  };

  // Delete handlers
  const handleDeleteClick = (topic) => {
    setSelectedTopic(topic);
    setShowDeleteModal(true);
  };

  const handleDeleteConfirm = async () => {
    if (!selectedTopic) return;

    try {
      await AdminNotesService.deleteTopic(selectedTopic.id);
      toast.success(`"${selectedTopic.title}" deleted successfully`);
      setShowDeleteModal(false);

      // Clear expanded items to force re-render
      setExpandedItems({});

      // Force reload with cache bust
      if (selectedCourse) {
        // Add timestamp to prevent caching
        await loadHierarchy(selectedCourse);
      }
    } catch (error) {
      toast.error("Failed to delete");
      console.error(error);
    }
  };

  const handleImportComplete = async () => {
    console.log("Import complete, refreshing content...");

    if (selectedCourse) {
      // Clear expanded items
      setExpandedItems({});

      // Force reload with fresh data
      await loadHierarchy(selectedCourse);

      // Increment refresh key to force re-render
      setRefreshKey((prev) => prev + 1);

      toast.success("Content refreshed with imported notes!");
    }
  };

  // Handle course selection - FIXED: Removed setCurrentPage(1)
  const handleCourseSelect = (courseId) => {
    setSelectedCourse(courseId.toString());
    // Do NOT reset pagination - keep user on current page
    // The selected course will be highlighted even on page 2
  };

  // Get status badge
  const getStatusBadge = (isPublished) => {
    return isPublished ? (
      <Badge bg="success" className="px-2 py-1">
        <FaCheckCircle className="me-1" size={10} /> Published
      </Badge>
    ) : (
      <Badge bg="secondary" className="px-2 py-1">
        <FaTimesCircle className="me-1" size={10} /> Draft
      </Badge>
    );
  };

  // Filter courses based on search
  const filteredCourses = courses.filter((course) =>
    course.name.toLowerCase().includes(searchTerm.toLowerCase()),
  );

  // Pagination logic
  const indexOfLastCourse = currentPage * coursesPerPage;
  const indexOfFirstCourse = indexOfLastCourse - coursesPerPage;
  const currentCourses = filteredCourses.slice(
    indexOfFirstCourse,
    indexOfLastCourse,
  );
  const totalPages = Math.ceil(filteredCourses.length / coursesPerPage);

  // Handle page change
  const paginate = (pageNumber) => setCurrentPage(pageNumber);

  // Render tree recursively
  const renderTree = (items, level = 0) => {
    return items.map((item) => {
      const hasChildren = item.children && item.children.length > 0;
      const isExpanded = expandedItems[item.id];
      const isFolder = item.content_type === "folder";

      return (
        <div
          key={item.id}
          className="admin-tree-item"
          style={{ marginLeft: `${level * 24}px`, marginBottom: "8px" }}
        >
          <div
            className="admin-tree-node"
            style={{
              backgroundColor: level === 0 ? "#f8f9fa" : "transparent",
              borderRadius: "8px",
              padding: "8px",
              transition: "all 0.2s",
            }}
          >
            <div className="admin-tree-node-content d-flex align-items-center">
              {hasChildren && (
                <button
                  className="admin-tree-chevron btn btn-link btn-sm p-0 me-2"
                  onClick={() => toggleExpand(item.id)}
                  style={{ width: "24px", textDecoration: "none" }}
                >
                  {isExpanded ? (
                    <FaChevronDown size={12} />
                  ) : (
                    <FaChevronRight size={12} />
                  )}
                </button>
              )}
              {!hasChildren && <span style={{ width: "24px" }} />}

              <span className="admin-tree-icon me-2">
                {isFolder ? (
                  isExpanded ? (
                    <FaFolderOpen className="text-warning" size={18} />
                  ) : (
                    <FaFolder className="text-warning" size={18} />
                  )
                ) : (
                  <FaBook className="text-primary" size={18} />
                )}
              </span>

              <span
                className="admin-tree-title flex-grow-1"
                style={{ fontWeight: isFolder ? "600" : "400" }}
              >
                {item.title}
              </span>

              <div className="admin-tree-badges d-flex gap-2 me-3">
                {getStatusBadge(item.is_published)}
                {isFolder && hasChildren && (
                  <Badge bg="info" className="px-2 py-1">
                    {item.children.length} items
                  </Badge>
                )}
              </div>

              <div className="admin-tree-actions">
                <Button
                  variant="outline-primary"
                  size="sm"
                  className="me-1"
                  onClick={() => navigate(`/admin/notes/edit/${item.id}`)}
                  title="Edit"
                >
                  <FaEdit />
                </Button>
                <Button
                  variant="outline-danger"
                  size="sm"
                  onClick={() => handleDeleteClick(item)}
                  title="Delete"
                >
                  <FaTrash />
                </Button>
              </div>
            </div>
          </div>

          {hasChildren && isExpanded && (
            <div className="admin-tree-children mt-1">
              {renderTree(item.children, level + 1)}
            </div>
          )}
        </div>
      );
    });
  };

  // Total items count
  const totalItems = (items) => {
    let count = items.length;
    items.forEach((item) => {
      if (item.children) {
        count += totalItems(item.children);
      }
    });
    return count;
  };

  return (
    <div>
      <Header />
      <div
        className="container-fluid p-4"
        style={{ backgroundColor: "#f5f7fb", minHeight: "100vh" }}
      >
        {/* Header Section */}
        <div className="d-flex justify-content-between align-items-center mb-4">
          <div>
            <Button
              variant="link"
              className="text-decoration-none p-0 mb-2 text-secondary"
              onClick={() => navigate("/admin")}
            >
              <FaArrowLeft className="me-2" /> Back to Dashboard
            </Button>
            <h1
              className="mb-0"
              style={{ fontSize: "28px", fontWeight: "600" }}
            >
              <FaBook className="me-2 text-primary" />
              Notes Management
            </h1>
            <p className="text-muted mt-2 mb-0">
              Create, organize, and manage educational content for your courses
            </p>
          </div>
          <div>
            {selectedCourse && (
              <Button
                variant="success"
                className="me-2"
                onClick={() => setShowImportModal(true)}
              >
                <FaCopy className="me-2" /> Import from Course
              </Button>
            )}
            <Button
              variant="primary"
              onClick={() => navigate("/admin/notes/create")}
            >
              <FaPlus className="me-2" /> Create New
            </Button>
          </div>
        </div>

        {/* Error Alert */}
        {error && (
          <Alert variant="danger" className="mb-4">
            <strong>Error:</strong> {error}
            <Button
              variant="outline-danger"
              size="sm"
              className="ms-3"
              onClick={() => selectedCourse && loadHierarchy(selectedCourse)}
            >
              Retry
            </Button>
          </Alert>
        )}

        {/* Course Selection Section - Card Grid */}
        <Card className="mb-4 shadow-sm border-0">
          <Card.Body>
            <div className="d-flex justify-content-between align-items-center mb-3">
              <div>
                <h5 className="mb-0">
                  <FaBook className="me-2 text-primary" />
                  Select a Course
                </h5>
                <small className="text-muted">
                  Choose a course to view and manage its notes
                </small>
              </div>
              <div className="text-muted">
                Total Courses: <strong>{courses.length}</strong>
                {searchTerm && (
                  <Badge bg="info" className="ms-2">
                    Filtered: {filteredCourses.length}
                  </Badge>
                )}
              </div>
            </div>

            {/* Search Bar */}
            <InputGroup className="mb-4">
              <InputGroup.Text style={{ backgroundColor: "white" }}>
                <FaSearch className="text-muted" />
              </InputGroup.Text>
              <Form.Control
                placeholder="Search courses by name..."
                value={searchTerm}
                onChange={(e) => {
                  setSearchTerm(e.target.value);
                  setCurrentPage(1); // Reset to first page only when searching
                }}
                style={{ borderLeft: "none" }}
              />
              {searchTerm && (
                <Button
                  variant="outline-secondary"
                  onClick={() => {
                    setSearchTerm("");
                    setCurrentPage(1);
                  }}
                >
                  Clear
                </Button>
              )}
            </InputGroup>

            {/* Course Cards Grid */}
            {loadingCourses ? (
              <div className="text-center py-5">
                <Spinner animation="border" variant="primary" />
                <p className="mt-2">Loading courses...</p>
              </div>
            ) : currentCourses.length === 0 ? (
              <Alert variant="info" className="text-center">
                <FaBook size={40} className="mb-3" />
                <h5>No courses found</h5>
                <p>
                  {searchTerm
                    ? `No courses matching "${searchTerm}"`
                    : "No courses available. Create a course first."}
                </p>
              </Alert>
            ) : (
              <>
                <Row className="g-3">
                  {currentCourses.map((course) => (
                    <Col md={6} lg={4} key={course.id}>
                      <Card
                        className={`h-100 cursor-pointer transition-all course-card ${
                          selectedCourse === course.id.toString()
                            ? "border-primary shadow-lg"
                            : "border-0 shadow-sm"
                        }`}
                        style={{
                          cursor: "pointer",
                          transition: "all 0.2s ease",
                        }}
                        onClick={() => handleCourseSelect(course.id)} // FIXED: Using new handler
                      >
                        <Card.Body>
                          <div className="d-flex justify-content-between align-items-start mb-2">
                            <div className="d-flex align-items-center">
                              <div
                                className="me-2"
                                style={{
                                  width: "40px",
                                  height: "40px",
                                  borderRadius: "8px",
                                  backgroundColor:
                                    selectedCourse === course.id.toString()
                                      ? "#0d6efd20"
                                      : "#f0f0f0",
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                }}
                              >
                                <FaBook
                                  size={20}
                                  className={
                                    selectedCourse === course.id.toString()
                                      ? "text-primary"
                                      : "text-secondary"
                                  }
                                />
                              </div>
                              <div>
                                <h6 className="mb-0 fw-semibold">
                                  {course.name}
                                </h6>
                                <small className="text-muted">
                                  {course.status === "active" ? (
                                    <Badge bg="success" className="mt-1">
                                      Active
                                    </Badge>
                                  ) : (
                                    <Badge bg="secondary" className="mt-1">
                                      Inactive
                                    </Badge>
                                  )}
                                </small>
                              </div>
                            </div>
                            {selectedCourse === course.id.toString() && (
                              <FaCheckCircle
                                className="text-primary"
                                size={20}
                              />
                            )}
                          </div>
                          <p className="text-muted small mb-0 mt-2">
                            {course.description?.substring(0, 80) ||
                              "No description"}
                            {course.description?.length > 80 && "..."}
                          </p>
                        </Card.Body>
                      </Card>
                    </Col>
                  ))}
                </Row>

                {/* Pagination - Only show if more than 1 page */}
                {totalPages > 1 && (
                  <div className="d-flex justify-content-center mt-4">
                    <Pagination>
                      <Pagination.First
                        onClick={() => paginate(1)}
                        disabled={currentPage === 1}
                      />
                      <Pagination.Prev
                        onClick={() => paginate(currentPage - 1)}
                        disabled={currentPage === 1}
                      />

                      {[...Array(totalPages)].map((_, index) => {
                        const pageNumber = index + 1;
                        // Show only 5 pages around current
                        if (
                          pageNumber === 1 ||
                          pageNumber === totalPages ||
                          (pageNumber >= currentPage - 1 &&
                            pageNumber <= currentPage + 1)
                        ) {
                          return (
                            <Pagination.Item
                              key={pageNumber}
                              active={pageNumber === currentPage}
                              onClick={() => paginate(pageNumber)}
                            >
                              {pageNumber}
                            </Pagination.Item>
                          );
                        } else if (
                          pageNumber === currentPage - 2 ||
                          pageNumber === currentPage + 2
                        ) {
                          return <Pagination.Ellipsis key={pageNumber} />;
                        }
                        return null;
                      })}

                      <Pagination.Next
                        onClick={() => paginate(currentPage + 1)}
                        disabled={currentPage === totalPages}
                      />
                      <Pagination.Last
                        onClick={() => paginate(totalPages)}
                        disabled={currentPage === totalPages}
                      />
                    </Pagination>
                  </div>
                )}

                {/* Current page indicator */}
                <div className="text-center mt-3">
                  <small className="text-muted">
                    Page {currentPage} of {totalPages} | Showing{" "}
                    {currentCourses.length} of {filteredCourses.length} courses
                  </small>
                </div>
              </>
            )}
          </Card.Body>
        </Card>

        {/* Content Section - Only show when course is selected */}
        {selectedCourse && (
          <>
            {/* Stats Dashboard */}
            <Row className="mb-4">
              <Col md={4}>
                <Card className="border-0 shadow-sm">
                  <Card.Body className="text-center">
                    <FaFolder className="text-warning mb-2" size={24} />
                    <h3 className="mb-0">{stats.totalFolders}</h3>
                    <small className="text-muted">Total Folders</small>
                  </Card.Body>
                </Card>
              </Col>
              <Col md={4}>
                <Card className="border-0 shadow-sm">
                  <Card.Body className="text-center">
                    <FaBook className="text-primary mb-2" size={24} />
                    <h3 className="mb-0">{stats.totalTopics}</h3>
                    <small className="text-muted">Total Topics</small>
                  </Card.Body>
                </Card>
              </Col>
              <Col md={4}>
                <Card className="border-0 shadow-sm">
                  <Card.Body className="text-center">
                    <FaChartLine className="text-success mb-2" size={24} />
                    <h3 className="mb-0">{totalItems(topics)}</h3>
                    <small className="text-muted">Total Items</small>
                  </Card.Body>
                </Card>
              </Col>
            </Row>

            {/* Course Info Banner */}
            <Card className="mb-4 shadow-sm border-0 bg-primary text-white">
              <Card.Body>
                <div className="d-flex justify-content-between align-items-center">
                  <div>
                    <h5 className="mb-0">📚 {selectedCourseDetails?.name}</h5>
                    <small className="opacity-75">
                      Managing notes and content for this course
                    </small>
                  </div>
                  <Button
                    variant="light"
                    size="sm"
                    onClick={() => navigate("/admin/notes/create")}
                  >
                    <FaPlus className="me-1" /> Add New Content
                  </Button>
                </div>
              </Card.Body>
            </Card>

            {/* Topics Tree */}
            <Card className="shadow-sm border-0">
              <Card.Header className="bg-white border-0 py-3">
                <div className="d-flex justify-content-between align-items-center">
                  <div>
                    <strong>📂 Course Content Structure</strong>
                    <small className="d-block text-muted mt-1">
                      Folders help organize topics. Click on folder icons to
                      expand/collapse.
                    </small>
                  </div>
                  <Badge bg="info" className="px-3 py-2">
                    <FaChevronCircleRight className="me-1" />
                    {totalItems(topics)} Total Items
                  </Badge>
                </div>
              </Card.Header>
              <Card.Body>
                {loading ? (
                  <div className="text-center py-5">
                    <Spinner animation="border" variant="primary" />
                    <p className="mt-2">Loading content structure...</p>
                  </div>
                ) : topics.length === 0 ? (
                  <Alert variant="info" className="text-center">
                    <FaFolder size={40} className="mb-3" />
                    <h5>No content found</h5>
                    <p>Create your first folder or topic to get started.</p>
                    <Button
                      variant="primary"
                      onClick={() => navigate("/admin/notes/create")}
                    >
                      Create Content
                    </Button>
                  </Alert>
                ) : (
                  <div
                    className="admin-tree-container"
                    style={{ maxHeight: "600px", overflowY: "auto" }}
                  >
                    {renderTree(topics)}
                  </div>
                )}
              </Card.Body>
            </Card>
          </>
        )}
      </div>

      {/* Delete Confirmation Modal */}
      <Modal
        show={showDeleteModal}
        onHide={() => setShowDeleteModal(false)}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title className="text-danger">
            <FaTrash className="me-2" /> Confirm Delete
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedTopic && (
            <>
              <p>Are you sure you want to delete:</p>
              <p className="fw-bold text-danger fs-5">{selectedTopic.title}</p>
              {selectedTopic.content_type === "folder" ? (
                <Alert variant="warning">
                  ⚠️ This folder contains {selectedTopic.children?.length || 0}{" "}
                  items. Deleting it will also delete all topics and sub-folders
                  inside it.
                </Alert>
              ) : (
                <Alert variant="warning">
                  ⚠️ This will also delete all content blocks and exercises
                  associated with this topic.
                </Alert>
              )}
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowDeleteModal(false)}>
            Cancel
          </Button>
          <Button variant="danger" onClick={handleDeleteConfirm}>
            Delete Permanently
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Import Notes Modal */}
      <ImportNotesModal
        show={showImportModal}
        onHide={() => setShowImportModal(false)}
        targetCourseId={selectedCourse}
        onImportComplete={handleImportComplete}
      />
    </div>
  );
};

export default NotesManagement;
